import React, { useState, useEffect, useCallback } from 'react';

import DashboardLayout from '../../layouts/DashboardLayout';
import { AdminService } from '../../../application/services/AdminService';
import { useToast } from '../../../context/ToastContext';
import { Building2, Save } from 'lucide-react';

import InformacionPrincipal from './components/InformacionPrincipal';
import ContactoUbicacion from './components/ContactoUbicacion';
import InformacionInstitucional from './components/InformacionInstitucional';
import DocumentosLegales from './components/DocumentosLegales';

const MiEmpresaPage = () => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [junta, setJunta] = useState(null);

    // Basic Form data
    const [nombre, setNombre] = useState('');
    const [razonSocial, setRazonSocial] = useState('');
    const [nombreComercial, setNombreComercial] = useState('');
    const [ruc, setRuc] = useState('');
    const [acuerdoMinisterial, setAcuerdoMinisterial] = useState('');
    const [fechaAcuerdo, setFechaAcuerdo] = useState('');
    
    // Contact & Location
    const [direccion, setDireccion] = useState('');
    const [provincia, setProvincia] = useState('');
    const [canton, setCanton] = useState('');
    const [parroquia, setParroquia] = useState('');
    const [comunidad, setComunidad] = useState('');
    const [telefono, setTelefono] = useState('');
    const [email, setEmail] = useState('');

    // Config data
    const [mision, setMision] = useState('');
    const [vision, setVision] = useState('');
    const [historia, setHistoria] = useState('');

    // Files
    const [reglamentoFile, setReglamentoFile] = useState(null);
    const [nominaFile, setNominaFile] = useState(null);
    const [estatutoFile, setEstatutoFile] = useState(null);

    const loadJunta = useCallback(async () => {
        try {
            setLoading(true);
            const data = await AdminService.getMiJunta();
            setJunta(data);
            if (data) {
                // Basic info
                setNombre(data.nombre || '');
                setRazonSocial(data.razon_social || '');
                setNombreComercial(data.nombre_comercial || '');
                setRuc(data.ruc || '');
                setAcuerdoMinisterial(data.acuerdo_ministerial || '');
                setFechaAcuerdo(data.fecha_acuerdo || '');
                
                // Location & Contact
                setDireccion(data.direccion || '');
                setProvincia(data.provincia || '');
                setCanton(data.canton || '');
                setParroquia(data.parroquia || '');
                setComunidad(data.comunidad || '');
                setTelefono(data.telefono || '');
                setEmail(data.email_institucional || '');

                if (data.configuracion) {
                    setMision(data.configuracion.mision || '');
                    setVision(data.configuracion.vision || '');
                    setHistoria(data.configuracion.historia || '');
                }
            }
        } catch (error) {
            toast.error(error);
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        loadJunta();
    }, [loadJunta]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const formData = new FormData();
            
            // Append all root fields
            formData.append('nombre', nombre);
            formData.append('razon_social', razonSocial);
            formData.append('nombre_comercial', nombreComercial);
            formData.append('ruc', ruc);
            formData.append('acuerdo_ministerial', acuerdoMinisterial);
            formData.append('fecha_acuerdo', fechaAcuerdo);
            formData.append('direccion', direccion);
            formData.append('provincia', provincia);
            formData.append('canton', canton);
            formData.append('parroquia', parroquia);
            formData.append('comunidad', comunidad);
            formData.append('telefono', telefono);
            formData.append('email_institucional', email);

            // Reconstruct configuracion ensuring we don't lose old data
            const newConfig = {
                ...(junta?.configuracion || {}),
                mision,
                vision,
                historia
            };
            
            formData.append('configuracion', JSON.stringify(newConfig));

            if (reglamentoFile) formData.append('archivo_reglamento', reglamentoFile);
            if (nominaFile) formData.append('archivo_nomina', nominaFile);
            if (estatutoFile) formData.append('archivo_estatuto', estatutoFile);

            await AdminService.updateMiJunta(formData);
            toast.success('Información actualizada exitosamente');
            
            // Reset files after upload
            setReglamentoFile(null);
            setNominaFile(null);
            setEstatutoFile(null);
            
            // Reload data
            loadJunta();
        } catch (error) {
            toast.error(error);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout title="Mi Empresa">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Mi Empresa">
            <div className="w-full pb-10 animate-fade-in">
                {/* Cabecera Informativa (Solo lectura) */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-5 md:p-6 text-white shadow-lg mb-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-white opacity-10"></div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-1">
                            <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                                <Building2 className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold">{junta?.nombre}</h1>
                                <p className="text-blue-100 text-xs mt-0.5">Configuración y Documentación Institucional</p>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                    
                    <InformacionPrincipal 
                        data={{ nombre, razonSocial, nombreComercial, ruc, acuerdoMinisterial, fechaAcuerdo }} 
                        handlers={{ setNombre, setRazonSocial, setNombreComercial, setRuc, setAcuerdoMinisterial, setFechaAcuerdo }} 
                    />

                    <ContactoUbicacion 
                        data={{ direccion, provincia, canton, parroquia, comunidad, telefono, email }} 
                        handlers={{ setDireccion, setProvincia, setCanton, setParroquia, setComunidad, setTelefono, setEmail }} 
                    />
                    
                    <InformacionInstitucional 
                        data={{ mision, vision, historia }} 
                        handlers={{ setMision, setVision, setHistoria }} 
                    />

                    <DocumentosLegales 
                        junta={junta}
                        files={{ reglamentoFile, nominaFile, estatutoFile }}
                        setFiles={{ setReglamentoFile, setNominaFile, setEstatutoFile }}
                        toast={toast}
                    />

                    {/* Acciones */}
                    <div className="flex justify-end pt-2">
                        <button 
                            type="submit" 
                            disabled={saving}
                            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-md shadow-blue-200 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Guardando cambios...
                                </>
                            ) : (
                                <>
                                    <Save className="w-5 h-5" />
                                    Guardar Configuración
                                </>
                            )}
                        </button>
                    </div>

                </form>
            </div>
        </DashboardLayout>
    );
};

export default MiEmpresaPage;
